import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../Firebase";
import { V3Project } from "../types/v3types";
import { useAuth } from "../hooks/useAuth";
import styled from "styled-components";

const Container = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
`;

const Title = styled.h1`
  font-size: 24px;
  margin-bottom: 30px;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: bold;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
`;

const TextArea = styled.textarea`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  resize: vertical;
  min-height: 100px;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
`;

const Button = styled.button`
  padding: 12px 24px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 10px;
`;

const AdminProjectCreate: React.FC = () => {
  const navigate = useNavigate();
  const { user: firebaseUser, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const [formData, setFormData] = useState({
    projectId: "",
    projectName: "",
    description: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!firebaseUser) {
        throw new Error("認証されていません。ページを再読み込みしてください。");
      }

      // Validate project ID
      if (!/^[a-zA-Z0-9-_]+$/.test(formData.projectId)) {
        throw new Error("プロジェクトIDは英数字、ハイフン、アンダースコアのみ使用できます");
      }

      const projectData: any = {
        projectId: formData.projectId,
        projectName: formData.projectName,
        ownerId: "admin",
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Only add description if it's not empty
      if (formData.description) {
        projectData.description = formData.description;
      }

      console.log("Creating project with data:", projectData);
      console.log("Firebase user:", firebaseUser);
      
      await setDoc(
        doc(db, "v3projects", formData.projectId),
        projectData
      );

      navigate(`/admin/v3/projects/${formData.projectId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "プロジェクトの作成に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Title>新規プロジェクト作成</Title>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="projectId">プロジェクトID（変更不可）</Label>
          <Input
            id="projectId"
            type="text"
            value={formData.projectId}
            onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
            placeholder="例: summer-event-2024"
            required
            disabled={loading}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="projectName">プロジェクト名</Label>
          <Input
            id="projectName"
            type="text"
            value={formData.projectName}
            onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
            placeholder="例: 夏のイベント 2024"
            required
            disabled={loading}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="description">説明（任意）</Label>
          <TextArea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="プロジェクトの説明を入力してください"
            disabled={loading}
          />
        </FormGroup>


        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Button type="submit" disabled={loading || authLoading}>
          {loading ? "作成中..." : authLoading ? "認証中..." : "プロジェクトを作成"}
        </Button>
      </Form>
    </Container>
  );
};

export default AdminProjectCreate;